import { MON, MON_FULL } from "./constants";
import type { DateRange } from "./types";

/** Length of the seeded daily series (days of synthetic history). */
export const N = 540;

/* ---------- Date system ---------- */
export const TODAY = new Date(2026, 4, 10);
export const DAY = 86400000;
export const EPOCH = new Date(TODAY.getTime() - N * DAY);
export const iso = (d: Date): string => d.toISOString().slice(0, 10);
/** Local-timezone YYYY-MM-DD (unlike `iso`, no UTC shift) — used for the
 *  real-calendar picker so a selected day never lands a day off. */
export const isoLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const fromIso = (s: string): Date => new Date(s + "T00:00:00");
export const idxFor = (d: Date): number => Math.max(0, Math.min(N - 1, Math.round((d.getTime() - EPOCH.getTime()) / DAY)));

/** The previous COMPLETE calendar month relative to `now` (the real current
 *  date). This is the dashboard's default range and what the metrics API is
 *  called with on load. */
export function lastCompletedMonth(now: Date = new Date()): DateRange {
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 → last day of prev month
  return { start: isoLocal(start), end: isoLocal(end) };
}

/**
 * Start date of a window spanning `buckets` whole monthly buckets and ENDING in
 * `anchorIso`'s month — the 1st of the month `buckets - 1` back. An 11-bucket
 * window off a 2026-07-30 anchor gives "2025-09-01" (Sep 2025 … Jul 2026).
 *
 * The argument is a BUCKET COUNT, not a lookback in months: `buckets` is exactly
 * how many months the resulting range covers inclusive of the anchor's own. Use
 * this rather than reading the earliest period off an API response, which
 * silently shortens the window whenever the oldest period comes back empty.
 */
export const monthBucketsStartIso = (anchorIso: string, buckets = 12): string => {
  const d = fromIso(anchorIso);
  if (Number.isNaN(d.getTime())) return anchorIso;
  return isoLocal(new Date(d.getFullYear(), d.getMonth() - (buckets - 1), 1));
};

/** `n` days from an ISO date, as a local YYYY-MM-DD; negative `n` goes back.
 *  Local-calendar arithmetic, so it never shifts a day across a zone boundary. */
export const addDaysIso = (isoDate: string, n: number): string => {
  const d = fromIso(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  d.setDate(d.getDate() + n);
  return isoLocal(d);
};

/** Last day of the calendar month `isoDate` falls in, as a local YYYY-MM-DD. */
export const monthEndIso = (isoDate: string): string => {
  const d = fromIso(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return isoLocal(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

/** `n` days before the real today, as a local YYYY-MM-DD. */
export const daysAgoIso = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoLocal(d);
};

// Selectable window for the date pickers: the last 2 years, up to today.
const NOW = new Date();
export const RANGE_MAX_ISO = isoLocal(NOW);
export const RANGE_MIN_ISO = isoLocal(new Date(NOW.getFullYear() - 2, NOW.getMonth(), NOW.getDate()));

/**
 * The warehouse refresh stamp (`config.dataRefreshDate`) as a local YYYY-MM-DD,
 * or null when the SP sent nothing usable. This is the LATEST day the pickers
 * may select: anything after it has no data behind it yet.
 *
 * The SP's own format is "07/13/2026 06:33 AM", but the value reaches us
 * verbatim and the backend stringifies a Date column as ISO, so both are read
 * here. An ISO stamp is truncated rather than re-parsed — `new Date()` on it
 * resolves in UTC, which lands a day early for anyone west of Greenwich.
 */
export const refreshDateIso = (raw: string | null | undefined): string | null => {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : isoLocal(d);
};

/** An ISO date pulled back to `maxIso` when it sits past it. */
export const clampDateIso = (isoDate: string, maxIso: string): string =>
  isoDate > maxIso ? maxIso : isoDate;

/**
 * A range slid back so it ends no later than `maxIso`, keeping its LENGTH —
 * "the last 7 days" stays seven days long when the max moves off today onto an
 * older refresh date, rather than collapsing to however much of the window is
 * still in bounds.
 */
export const clampRangeIso = (r: DateRange, maxIso: string): DateRange => {
  if (r.end <= maxIso) return r;
  const shift = Math.round((fromIso(maxIso).getTime() - fromIso(r.end).getTime()) / DAY);
  const start = addDaysIso(r.start, shift);
  return { start: start < RANGE_MIN_ISO ? RANGE_MIN_ISO : start, end: maxIso };
};

/** Default range = last completed month, resolved at load. */
export const DEFAULT_RANGE: DateRange = lastCompletedMonth(NOW);

export type Granularity = "day" | "week" | "month";

export function rangeDayCount(r: DateRange): number {
  return Math.round((fromIso(r.end).getTime() - fromIso(r.start).getTime()) / DAY) + 1;
}
export function granularity(r: DateRange): Granularity {
  const n = rangeDayCount(r);
  if (n < 7) return "day";
  if (n < 30) return "week";
  return "month";
}
export const trendUnitLabel = (g: Granularity): string =>
  g === "day" ? "Trailing 12 days" : g === "week" ? "Trailing 12 weeks" : "Trailing 12 months";

/**
 * Start of a trailing window of `buckets` whole buckets at `grain`, ending at
 * `anchorIso` — the counterpart of {@link monthBucketsStartIso} for the other
 * two grains, so a drill opened from a Week or Day view spans twelve WEEKS or
 * twelve DAYS back from the "as of" date rather than twelve months.
 *
 * `buckets` is a bucket COUNT inclusive of the anchor's own bucket: 12 days off
 * a 2026-08-27 anchor starts 2026-08-16, and 12 weeks starts 2026-06-05.
 */
export const trailingBucketsStartIso = (anchorIso: string, grain: Granularity, buckets = 12): string =>
  grain === "month"
    ? monthBucketsStartIso(anchorIso, buckets)
    : addDaysIso(anchorIso, -((grain === "week" ? 7 * buckets : buckets) - 1));

/**
 * The grain a returned series is bucketed at, read off the SPACING of its
 * period starts. The summary/relations SPs pick their own bucketing from the
 * range they are called with and don't say which they chose, so the labels have
 * to infer it — otherwise twelve daily buckets all render as the same "Aug '26".
 *
 * The SMALLEST gap decides, so one missing bucket in the middle can't promote a
 * daily series to weekly. A series too short to have a gap reads as monthly, the
 * common case everywhere but the All Metrics Week/Day drills.
 */
export function seriesGranularity(isoDates: string[]): Granularity {
  const times = isoDates
    .map((d) => fromIso(String(d).slice(0, 10)).getTime())
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b);
  let gap = Infinity;
  for (let i = 1; i < times.length; i++) gap = Math.min(gap, (times[i] - times[i - 1]) / DAY);
  if (!Number.isFinite(gap)) return "month";
  if (gap <= 1) return "day";
  if (gap <= 7) return "week";
  return "month";
}

export function formatDateRange(r: DateRange): string {
  const a = fromIso(r.start), b = fromIso(r.end);
  // A single day states itself once — the By Day records drill spans exactly one
  // date, and "Aug 27 – Aug 27, 2026" reads as a range that isn't one.
  if (r.start === r.end) return `${MON[a.getMonth()]} ${a.getDate()}, ${a.getFullYear()}`;
  // A full calendar month shows as "June 2026" rather than a day span.
  const sameMonth = a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
  const lastOfMonth = new Date(b.getFullYear(), b.getMonth() + 1, 0).getDate();
  if (sameMonth && a.getDate() === 1 && b.getDate() === lastOfMonth) {
    return `${MON_FULL[a.getMonth()]} ${a.getFullYear()}`;
  }
  // Both endpoints carry their year once the range crosses one: "Sep 1, 2025 –
  // Aug 27, 2026", not "Sep 1 – Aug 27, 2026", which reads as a five-month span
  // inside the END year. Within a single year the start year is redundant and
  // the trailing one still spells it out.
  const startYear = a.getFullYear() === b.getFullYear() ? "" : `, ${a.getFullYear()}`;
  return `${MON[a.getMonth()]} ${a.getDate()}${startYear} – ${MON[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;
}

/** An ISO date → "Aug 01, 2026". The long form used inside the date-range
 *  picker, where each endpoint has to read unambiguously on its own. */
export const formatDateLong = (isoDate: string): string => {
  const d = fromIso(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return `${MON[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}, ${d.getFullYear()}`;
};

/** Both endpoints spelled out in full — "Aug 01, 2026 - Aug 04, 2026". The
 *  picker's own label; unlike {@link formatDateRange} it never collapses a whole
 *  calendar month to "August 2026", since the trigger has to say exactly which
 *  days the popup will open on. */
export const formatDateRangeLong = (r: DateRange): string =>
  `${formatDateLong(r.start)} - ${formatDateLong(r.end)}`;

/**
 * A trend period's ISO start date → "May '25". The one period label used
 * everywhere a monthly series is shown — chart axes, trend tables and the CSV
 * exports alike — because a trailing window can span a year boundary and a bare
 * month reads as the current year. Falls back to the raw string when
 * unparseable.
 */
export const periodLabelWithYear = (isoDate: string, grain: Granularity = "month"): string => {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(isoDate || "");
  if (!m) return isoDate;
  const mon = MON[Number(m[2]) - 1], yy = m[1].slice(2);
  // Weekly / daily buckets need the DAY too — a month alone repeats itself
  // across a twelve-week or twelve-day window. Matches the All Metrics column
  // headers ("Jun 1 '25").
  return grain === "month" || !m[3] ? `${mon} '${yy}` : `${mon} ${Number(m[3])} '${yy}`;
};

/**
 * The CHART-AXIS form of {@link periodLabelWithYear}: same label, minus the
 * year on week/day series ("Jul 20", not "Jul 20 '26").
 *
 * A twelve-point daily axis gets ~42px per tick, and "Jul 20 '26" needs more
 * than that — the labels ran into each other. The year is what gets dropped
 * because the axis is the one place it is redundant: the panel's date-range
 * chip above the chart already says which year is on screen. Monthly labels
 * keep it, since a trailing twelve months genuinely spans two.
 *
 * Anywhere a label is read WITHOUT that chip next to it — CSV exports, the
 * Table tab — keep using {@link periodLabelWithYear}.
 */
export const periodAxisLabel = (isoDate: string, grain: Granularity = "month"): string => {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(isoDate || "");
  if (!m) return isoDate;
  if (grain === "month" || !m[3]) return periodLabelWithYear(isoDate, grain);
  return `${MON[Number(m[2]) - 1]} ${Number(m[3])}`;
};

export interface PeriodInfo {
  periodStart: Date;
  periodEnd: Date;
  periodLabel: string;
  granularity: Granularity;
  granLabel: string;
}

export function latestPeriod(pivot: string, anchorIso?: string): PeriodInfo {
  const a = anchorIso ? new Date(anchorIso + "T00:00:00") : new Date(TODAY.getTime());
  if (pivot === "month") {
    const start = new Date(a.getFullYear(), a.getMonth(), 1), end = new Date(a.getFullYear(), a.getMonth() + 1, 0);
    return { periodStart: start, periodEnd: end, periodLabel: `${MON_FULL[start.getMonth()]} ${start.getFullYear()}`, granularity: "month", granLabel: "Monthly" };
  }
  if (pivot === "week") {
    const end = a, start = new Date(a.getTime() - 6 * DAY);
    return { periodStart: start, periodEnd: end, periodLabel: `Week of ${MON[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()}`, granularity: "week", granLabel: "Weekly" };
  }
  return { periodStart: a, periodEnd: a, periodLabel: `${MON_FULL[a.getMonth()]} ${a.getDate()}, ${a.getFullYear()}`, granularity: "day", granLabel: "Daily" };
}
